/**
 * The desktop agent's health report carries one diagnostic line in `lastError`. Today that line names the machine
 * files whose folder the agent does not trust (desktop/src/Gyro.Core/Config/MachinePathTrust.cs,
 * MachinePathTrustReport.HealthSummary), for example:
 *
 *   machine-path-trust report-only: policy UntrustedOwner at C:\ProgramData\Exante360\Gyro; push-key OtherWriter at C:\...
 *
 * describeDeviceHealthNote turns that line into words an IT admin can act on. The agent cuts the line at 480
 * characters and gyro-svc at 500, so the last item may be cut short. Any other line is shown as the agent sent it.
 */

export interface DeviceHealthNote {
  /** What the Support screen shows under the health badge. */
  text: string;
  /** The line as the agent sent it (control characters removed, length bounded), for the tooltip. */
  raw: string;
  /** 'warn': the check only reports (this release). 'bad': the check refused the files. 'muted': a line this console does not know. */
  tone: 'warn' | 'bad' | 'muted';
}

const PREFIX = /^machine-path-trust (enforced|report-only): (.+)$/;
const ITEM = /^([a-z][a-z-]*) ([A-Za-z]+) at (.+)$/;
const MAX_TEXT = 600;
// MachinePathTrustReport.MaxHealthChars: a line this long was cut by the agent, so its last item may be incomplete.
const AGENT_CAP = 480;

const FILE_WORDS: Record<string, string> = {
  policy: 'machine policy',
  'push-key': 'push signing key',
  'release-key': 'release signing key',
  'ipc-key': 'broker channel key',
};

// MachinePathTrustState, minus Trusted and Missing, which the agent never reports in this line.
const STATE_WORDS: Record<string, string> = {
  UntrustedOwner: 'its folder is not owned by SYSTEM or Administrators',
  OtherWriter: 'someone other than IT can change it or its folder',
  ReparsePoint: 'there is a link (junction or symbolic link) on the way to it',
  Unreadable: 'its folder permissions could not be read',
};

function clean(value: string): string {
  const text = value.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > MAX_TEXT ? `${text.slice(0, MAX_TEXT - 1)}…` : text;
}

/** Null when the report carries no line. */
export function describeDeviceHealthNote(lastError: string | null | undefined): DeviceHealthNote | null {
  if (typeof lastError !== 'string') return null;
  const raw = clean(lastError);
  if (!raw) return null;
  const unknown: DeviceHealthNote = { text: `Agent note: ${raw}`, raw, tone: 'muted' };

  const line = PREFIX.exec(raw);
  if (!line) return unknown;
  const enforced = line[1] === 'enforced';
  const parts = line[2].split('; ');
  const files: string[] = [];
  const folders = new Set<string>();
  let cutShort = false;
  if (raw.length >= AGENT_CAP && parts.length > 1) {
    // Even when it still parses, a cut item names a wrong folder ("C:\Prog") or state, so it is not shown.
    parts.pop();
    cutShort = true;
  }
  for (const [index, part] of parts.entries()) {
    const item = ITEM.exec(part);
    if (!item) {
      // Only the last item can be cut short by the length limits; anything else is a line this console does not know.
      if (index === parts.length - 1 && files.length > 0) {
        cutShort = true;
        continue;
      }
      return unknown;
    }
    const [, name, state, folder] = item;
    files.push(`${FILE_WORDS[name] ?? name} (${STATE_WORDS[state] ?? state})`);
    folders.add(folder);
  }
  if (files.length === 0) return unknown;

  const lead = enforced
    ? 'Refused by the machine folder check'
    : 'Would be refused once the machine folder check is enforced';
  const more = cutShort ? ', and more (the report was cut short)' : '';
  const where = folders.size === 1 ? `Folder: ${[...folders][0]}.` : `Folders: ${[...folders].join(', ')}.`;
  const text = `${lead}: ${files.join('; ')}${more}. ${where} Provisioning the PC through Intune repairs the folder.`;
  return { text: clean(text), raw, tone: enforced ? 'bad' : 'warn' };
}
